/**
 * Unified Auth Token & Storage Management Utility
 * Keeps localStorage, cookies, and UI components in sync across tabs and sessions.
 */

import { getApiUrl } from "@/lib/api-config";

const TOKEN_KEY = "access_token";
const PROFILE_KEY = "cached_customer_profile";
// 14 days in seconds (matches backend JWT_REFRESH_TTL)
const COOKIE_MAX_AGE = 1209600;

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;

  const localToken = localStorage.getItem(TOKEN_KEY);
  if (localToken) return localToken;

  // Fallback to cookie if available
  const match = document.cookie.match(new RegExp("(?:^|; )" + TOKEN_KEY + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

export function setAuthToken(token: string): void {
  if (typeof window === "undefined") return;

  localStorage.setItem(TOKEN_KEY, token);
  document.cookie = `${TOKEN_KEY}=${encodeURIComponent(
    token
  )}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;

  window.dispatchEvent(
    new CustomEvent("auth:change", { detail: { isLoggedIn: true, token } })
  );
}

let cachedProfileData: unknown = null;

export function getCachedProfile(): unknown {
  if (cachedProfileData !== null) return cachedProfileData;
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (raw) {
      cachedProfileData = JSON.parse(raw);
      return cachedProfileData;
    }
  } catch {
    // Ignore JSON parse error
  }
  return null;
}

export function clearAuthToken(): void {
  if (typeof window === "undefined") return;

  cachedProfileData = null;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PROFILE_KEY);
  sessionStorage.clear();
  document.cookie = `${TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

  window.dispatchEvent(
    new CustomEvent("auth:change", { detail: { isLoggedIn: false, token: null } })
  );
}

export async function logoutUser(): Promise<void> {
  if (typeof window === "undefined") return;
  const token = getAuthToken();
  if (token) {
    try {
      await fetch(getApiUrl("/api/v1/auth/logout"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    } catch {
      // Ignore errors when notifying backend on logout
    }
  }
  clearAuthToken();
}

export function onAuthChange(callback: (isLoggedIn: boolean) => void): () => void {
  if (typeof window === "undefined") return () => {};

  const handleCustomEvent = (event: Event) => {
    const customEvt = event as CustomEvent<{ isLoggedIn: boolean }>;
    callback(customEvt.detail?.isLoggedIn ?? !!getAuthToken());
  };

  const handleStorageEvent = (event: StorageEvent) => {
    if (event.key === TOKEN_KEY) {
      callback(!!event.newValue);
    }
  };

  window.addEventListener("auth:change", handleCustomEvent);
  window.addEventListener("storage", handleStorageEvent);

  return () => {
    window.removeEventListener("auth:change", handleCustomEvent);
    window.removeEventListener("storage", handleStorageEvent);
  };
}

export function emitProfileChange(profile?: unknown): void {
  if (profile !== undefined) {
    cachedProfileData = profile;
    if (typeof window !== "undefined") {
      try {
        if (profile) {
          localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
        } else {
          localStorage.removeItem(PROFILE_KEY);
        }
      } catch {
        // Ignore quota errors
      }
    }
  }
  if (typeof window === "undefined") return;
  setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent("profile:change", { detail: { profile } })
    );
  }, 0);
}

export function onProfileChange(callback: (profile?: unknown) => void): () => void {
  if (typeof window === "undefined") return () => {};

  const handleCustomEvent = (event: Event) => {
    const customEvt = event as CustomEvent<{ profile?: unknown }>;
    callback(customEvt.detail?.profile);
  };

  window.addEventListener("profile:change", handleCustomEvent);
  return () => {
    window.removeEventListener("profile:change", handleCustomEvent);
  };
}
